import ServiceCollection from "src/services/ServiceCollection";
import {
    Button, Container,
    ExpandableSection,
    FormField,
    Grid,
    Header,
    Input,
    Select,
    SpaceBetween,
} from "@amzn/awsui-components-react/polaris";
import TPEAction from "src/models/common/TPEAction";
import React, {useEffect, useState} from "react";
import useReducerWithLogger from "src/services/utils/ReducerWithLogger";
import {reportsReducer} from "src/services/reports/ReportsReducer";
import {initialState, ReportsState} from "src/services/reports/ReportsState";
import {
    GetCLIsByProviderCompanyCodeRequest,
    TPTaxReportDropdownOptions, TPTaxReportMapResponse
} from "src/models/reports/TPTaxReport";
import CONSTANTS from "src/utils/constants";
import {SelectProps, Tiles} from "@amzn/awsui-components-react";
import TPTaxReportTableGrid from "src/components/reports/TPTaxReportTableGrid";
import {FetchReportRequest} from "src/models/reports/FetchReport";
import {GenerateReportRequest, ReportRequestParams} from "src/models/reports/GenerateReport";
import useLocalStorage from "src/utils/useLocalStorage";

export type ContextType = {
    state: ReportsState,
    dispatch: React.Dispatch<TPEAction>,
    services: ServiceCollection,
}

const TPTaxReportProvider = (props: {
    state: ReportsState, dispatch: React.Dispatch<TPEAction>, services: ServiceCollection,
    children: JSX.Element
}) => {
    const {state, dispatch, services, children} = props;
    const providerValue = React.useMemo(() => ({
        state, dispatch, services
    }), [state, dispatch]);
    return (
        <TPTaxReportContext.Provider value={providerValue}>
            {children}
        </TPTaxReportContext.Provider>
    );
}

export const TPTaxReportContext = React.createContext(null as ContextType | null);
export default function TPTaxReportView(props: { services: ServiceCollection}) {
    const {services} = props;
    const [state, dispatch] = useReducerWithLogger(reportsReducer, initialState);
    const [oracleId, setOracleId] = React.useState("");
    const [selectedCLIOption, setSelectedCLIOption] = React.useState(null as SelectProps.Option | null);
    const [selectedPeriodNameOption, setSelectedPeriodNameOption] = React.useState(null as SelectProps.Option | null);
    const [cliRequestPayload, setCliRequestPayload] = useState(null as GetCLIsByProviderCompanyCodeRequest | null);
    const [optionList, setOptionList] = React.useState([] as TPTaxReportDropdownOptions[]);
    const [dropDownDisabledFlag, setDropDownDisabledFlag] = React.useState(true);
    const [fetchReportRequest, setFetchReportRequest] = React.useState(null as FetchReportRequest | null);
    const [reportContent, setReportContent] = React.useState(null as TPTaxReportMapResponse | null);
    const [generateReportRequest, setGenerateReportRequest] = React.useState(null as GenerateReportRequest | null);
    const [clisResponse, getCLIsLoading, getCLIsError] = services.reportsService.getCLIsByProviderCompanyCode(cliRequestPayload);
    const [previousReportResponse, getPreviousReportLoading, getPreviousReportError] = services.reportsService.fetchReportData(fetchReportRequest);
    const [generateReportResponse, generateReportLoading, generateReportError] = services.reportsService.generateReport(generateReportRequest);
    const [accountingPeriodResult, accountingPeriodLoading, accountingPeriodError] = services.reportsService.getReportingPeriods();
    const [reportCategory, setReportCategory] = React.useState(CONSTANTS.TP_TAX_REPORT_CATEGORY.ALLOCATION);

    useEffect(() => {
        if (!clisResponse) {
            return;
        }

        setOptionList([
        //     { will be giving this functionality later
        //     label: 'SUMMARY',
        //     value: 'SUMMARY'
        // },
            ...clisResponse.CLIList.map(value => ({
                label: value,
                value: value
            })),]);

    }, [clisResponse]);

    useEffect( () => {
        if(!previousReportResponse) {
            return;
        }

        if(previousReportResponse.statusMessage === null){
            setReportContent(JSON.parse(previousReportResponse.payload));
        }else{
            services.messageService.showErrorAutoDismissBanner(previousReportResponse.statusMessage)
        }

    }, [previousReportResponse])

    useEffect(() => {
        if(!generateReportResponse) {
            return;
        }

        if(generateReportResponse.reportId)
            services.messageService.showInfoAutoDismissBanner("Generating Report!")
    }, [generateReportResponse])

    useEffect(() => {
        if (!getCLIsError) {
            return;
        }
        services.messageService.showErrorAutoDismissBanner(getCLIsError);
    }, [getCLIsError])

    useEffect(() => {
        if (!getPreviousReportError) {
            return;
        }
        services.messageService.showErrorAutoDismissBanner(getPreviousReportError);
    }, [getPreviousReportError])

    useEffect(() => {
        if (!generateReportError) {
            return;
        }
        services.messageService.showErrorAutoDismissBanner(generateReportError);
    }, [generateReportError])

    const onFetchOptionsClicked = () => {
        if (!!oracleId) {
            resetOnFetchOptionButtonClicked();
            setCliRequestPayload({
                providerCompanyCode: oracleId,
                calculationStatus: CONSTANTS.CALCULATION_STATUS.ACTIVE
            })
            setDropDownDisabledFlag(false);
        }
    }

    const onResetButtonClicked = () => {
        setOracleId("")
        setDropDownDisabledFlag(true);
        setOptionList([]);
        setReportContent(null);
        setSelectedCLIOption(null);
        setSelectedPeriodNameOption(null);
    }
    const resetOnFetchOptionButtonClicked = () => {
        setDropDownDisabledFlag(true);
        setOptionList([]);
        setReportContent(null);
        setSelectedCLIOption(null);
        setSelectedPeriodNameOption(null);
      };
    function fetchReportRequestParams(): ReportRequestParams {
        return {
            "providerCompanyCode": oracleId,
            "tpTaxReportFor": selectedCLIOption?.value as string,
            "period": selectedPeriodNameOption?.value as string
        }
    }

    return (
        <TPTaxReportProvider services={services} state={state} dispatch={dispatch}>
            <div>
                <SpaceBetween size="l" direction="vertical">
                    <Container
                        header={
                            <Header
                                actions={
                                    <SpaceBetween
                                        direction="horizontal"
                                        size="xs"
                                    >
                                        <Button variant="link" onClick={onResetButtonClicked}>Reset</Button>
                                        <Button onClick={() => {
                                            setFetchReportRequest({
                                                reportName: CONSTANTS.REPORT_TYPES.TP_TAX,
                                                params: fetchReportRequestParams()
                                            })
                                        }}
                                                disabled={selectedCLIOption === null || selectedPeriodNameOption == null}
                                        >View previous report</Button>
                                        <Button variant="primary" onClick={() => {
                                            setGenerateReportRequest({
                                                userId: useLocalStorage.getUsernameFromLocalStorage(),
                                                reportName: CONSTANTS.REPORT_TYPES.TP_TAX,
                                                params: fetchReportRequestParams()
                                            })
                                        }}
                                                disabled={selectedCLIOption === null || selectedPeriodNameOption == null}
                                        >Generate new report</Button>
                                    </SpaceBetween>}>

                                Report Criteria
                            </Header>
                        }
                    >
                        <div>
                            <Grid
                                gridDefinition={[{colspan: 3}, {colspan: 2}, {colspan: 2}, {colspan: 2}]}
                            >
                                    <FormField label ="Oracle Id">
                                        <Input
                                            onChange={({detail}) => {
                                                const oracleIdInputWithoutSpaces = detail.value.replace(/\s/g, ''); // remove all spaces from the input
                                            setOracleId(oracleIdInputWithoutSpaces)
                                            }}
                                            value={oracleId}
                                            placeholder="Type the Oracle Id"
                                        />
                                    </FormField>

                                    <SpaceBetween
                                        direction="vertical"
                                        size="xxs">
                                        <FormField label="&nbsp;">
                                        <Button variant="primary" disabled={!oracleId}
                                                onClick={onFetchOptionsClicked}>
                                            Fetch Options</Button>
                                        </FormField>
                                    </SpaceBetween>
                                    <SpaceBetween
                                        direction="vertical"
                                        size="xxs">
                                        <FormField label="CLI">
                                        <Select
                                            statusType={optionList.length == 0 ? "loading" : "finished"}
                                            disabled={dropDownDisabledFlag}
                                            loadingText="Fetching available options"
                                            selectedOption={selectedCLIOption}
                                            onChange={({detail}) =>
                                                setSelectedCLIOption(detail.selectedOption)
                                            }
                                            options={optionList}
                                            selectedAriaLabel="Selected"
                                            placeholder="Select the CLI for report"
                                            virtualScroll={true}
                                            filteringType="auto"
                                        />
                                        </FormField>
                                    </SpaceBetween>
                                    <SpaceBetween
                                        direction="vertical"
                                        size="xxs">
                                        <FormField label="Accounting Period">
                                        <Select
                                            statusType={accountingPeriodLoading ? "loading" : "finished"}
                                            disabled={dropDownDisabledFlag}
                                            loadingText="Fetching available options"
                                            selectedOption={selectedPeriodNameOption}
                                            onChange={({detail}) =>
                                                setSelectedPeriodNameOption(detail.selectedOption)
                                            }
                                            options={
                                                accountingPeriodResult == null
                                                  ? []
                                                  : accountingPeriodResult.ReportingPeriodList.map(
                                                      (accountingPeriodNameValue) => ({
                                                        label: accountingPeriodNameValue,
                                                        value: accountingPeriodNameValue,
                                                      })
                                                    )
                                              }
                                            placeholder="Select the accounting period"
                                            virtualScroll={true}
                                            filteringType="auto"
                                        />
                                        </FormField>
                                    </SpaceBetween>
                            </Grid>
                        </div>
                    </Container>

                    {reportContent && <>
                        <Tiles
                            onChange={({detail}) => setReportCategory(detail.value)}
                            value={reportCategory}
                            items={[
                                {
                                    label: CONSTANTS.TP_TAX_REPORT_CATEGORY.MARGIN,
                                    value: CONSTANTS.TP_TAX_REPORT_CATEGORY.MARGIN
                                },
                                {
                                    label: CONSTANTS.TP_TAX_REPORT_CATEGORY.ALLOCATION,
                                    value: CONSTANTS.TP_TAX_REPORT_CATEGORY.ALLOCATION
                                },
                            ]}
                        />

                        <Container
                            header={
                                <Header>
                                    {reportCategory + " Report"} 
                                </Header>
                            }
                        >
                            {reportCategory == CONSTANTS.TP_TAX_REPORT_CATEGORY.MARGIN &&
                                <>
                                    <ExpandableSection variant={'container'} headerText="Details of CLI">
                                        <TPTaxReportTableGrid data={reportContent?.DETAIL} services={services}/>
                                    </ExpandableSection>
                                    <ExpandableSection variant={'container'} headerText="Balances">
                                        <TPTaxReportTableGrid data={reportContent?.BALANCES} services={services}/>
                                    </ExpandableSection>
                                    <ExpandableSection variant={'container'} headerText="Consolidated">
                                        <TPTaxReportTableGrid data={reportContent?.CONSOLIDATED} services={services}/>
                                    </ExpandableSection>
                                    <ExpandableSection variant={'container'} headerText="PLI">
                                        <TPTaxReportTableGrid data={reportContent?.PLI} services={services}/>
                                    </ExpandableSection>
                                </>
                            }
                            {reportCategory == CONSTANTS.TP_TAX_REPORT_CATEGORY.ALLOCATION &&
                                <>
                                    <ExpandableSection variant={'container'} headerText="Details of CLI">
                                        <TPTaxReportTableGrid data={reportContent?.DETAIL} services={services}/>
                                    </ExpandableSection>
                                    <ExpandableSection variant={'container'} headerText="Allocation">
                                        <TPTaxReportTableGrid data={reportContent?.ALLOCATION} services={services}/>
                                    </ExpandableSection>
                                </>
                            }
                        </Container>
                    </>}
                </SpaceBetween>
            </div>
        </TPTaxReportProvider>
    )
}